import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Check, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import {
  applyOpeningVariant,
  bodyToText,
  coupleFullName,
  coupleShortName,
  coverageByCategory,
  formatWeddingHours,
  isPerRecipientVariable,
  MAX_BATCH_SIZE,
  plainTextToHtml,
  renderTemplate,
  vendorGreetingName,
  type BodyFormat,
  type MergeContext,
} from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import AddVendorDialog from '@/components/AddVendorDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import MessageSheet from '@/components/MessageSheet';
import { stripVariableChips } from '@/components/RichBodyEditor';
import RolodexEmpty from '@/components/RolodexEmpty';
import RolodexPicker from '@/components/RolodexPicker';
import VendorRecipientField, { type VendorPrefill } from '@/components/VendorRecipientField';
import { variableLabel } from '@/components/VariableField';
import { Card } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import type { VendorOption } from '@/lib/vendor-search';
import { formatDate } from '@/lib/date';

/**
 * The batch composer (PRD §F3).
 *
 * The rule that shapes this whole screen: **every message is individually
 * previewed and independently editable before it goes**. Not a preview of the
 * template with a recipient count — a stack of the actual messages, each one
 * exactly as that vendor will receive it. A planner who cannot see the eight
 * messages cannot take responsibility for them, and this product's entire
 * proposition is that it does not embarrass you in front of your vendors.
 */

/** A token with its punctuation taken off — `{{venue}}` reads as `venue`. For
 *  the places too small to carry a chip. */
const bareTokens = (text: string) => text.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, '$1');

export default function ComposePage() {
  const { id: weddingId } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const category = params.get('category') ?? '';

  const [step, setStep] = useState<'compose' | 'review'>('compose');
  /**
   * Gmail's daily cap, surfaced only at the moment it blocks something — and
   * which block it was, because the way out differs: a refused pick leaves a
   * batch that can still go today, while a batch that outgrew the cap while
   * she wrote has to lose a message or wait.
   */
  const [limitHit, setLimitHit] = useState<'pick' | 'batch' | null>(null);
  /** Whether the review step has already said so for the batch it is on. */
  const warnedOverQuota = useRef(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  /**
   * The template as customized for *this* wedding — the step between choosing
   * a template and previewing eight messages.
   *
   * Null until the override query and the template have both resolved, because
   * seeding it from the template before the saved customization arrives would
   * flash the wrong text and, worse, be what a quick planner saves over the
   * top of.
   */
  const [draft, setDraft] = useState<{
    subject: string;
    body: string;
    bodyFormat: BodyFormat;
  } | null>(null);
  /**
   * The one piece of text on the pick step. It drives the recipient field's
   * dropdown *and* narrows the grid in the browse sheet, so a planner who
   * types "blu" does not have to type it again to see the browse list agree
   * with her. Held here rather than inside either child for that reason alone.
   * Surviving the dropdown's close is `VendorRecipientField`'s job, not this
   * page's.
   */
  const [query, setQuery] = useState('');
  /**
   * The same text, held still long enough to ask the server about it.
   *
   * Debounced only to avoid a request per keystroke — the contacts endpoint is
   * a single indexed query against her own rows and needs no protecting. 150ms
   * is the Add Vendor dialog's figure too, and under the ~200ms at which a list
   * stops feeling like it is keeping up.
   *
   * Trimmed, because `VendorRecipientField` decides whether to show contact
   * rows from the trimmed query — a lone space must not be the thing that fires
   * a request for the top of the ranking.
   */
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 150);
    return () => clearTimeout(timer);
  }, [query]);
  /**
   * Which slice of the rolodex the grid is showing. Starts on the category
   * being inquired about, because that is nearly always who the planner came
   * to write to — but it is a filter, not the boundary of the list. A planner
   * who wants a florist she filed under "Floral & Styling", or a caterer she
   * also wants to ask about flowers, clears this and sees everyone.
   *
   * Up here rather than inside the grid for the same reason `query` is: the
   * sheet the grid lives in unmounts its content when it closes, so state
   * owned by the grid is thrown away every time the planner shuts the panel to
   * look at the chips she has built. Widening to the whole rolodex is
   * deliberate work, and it has to still be there when she opens the panel to
   * pick a second vendor.
   */
  const [categoryFilter, setCategoryFilter] = useState(category);
  /** Is the browse sheet open? Shut by default — the To line answers the
   *  common case, and the rolodex is not what the planner came to read. */
  const [browsing, setBrowsing] = useState(false);
  /**
   * What a new vendor is being created from: `null` is "no dialog", `{}` is
   * "opened bare, from the grid's Add vendor button", and anything else is the
   * prefill the recipient field asked for — the text the planner typed that
   * nothing answered to, or both halves of a contact she picked.
   *
   * One piece of state rather than an `addingVendor` boolean beside a prefill,
   * because the two can only ever be set and cleared together, and a dialog
   * open with a stale prefill is the failure that shape invites.
   */
  const [creatingFrom, setCreatingFrom] = useState<VendorPrefill | null>(null);
  /** Per-vendor overrides. Absent means "still the rendered template". */
  const [edits, setEdits] = useState<Record<string, { subject: string; body: string }>>({});
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: wedding } = useQuery({
    queryKey: ['wedding', weddingId],
    queryFn: async () => {
      const res = await client.api.weddings[':id'].$get({ param: { id: weddingId! } });
      if (!res.ok) throw new Error('Failed to load wedding');
      return res.json();
    },
    enabled: !!weddingId,
  });

  const { data: settings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to load settings');
      return res.json();
    },
  });

  /**
   * The whole rolodex, filtered down in the browser rather than by the server.
   *
   * Narrowing this to the inquired category server-side is what made the
   * category the ceiling instead of the starting point: clearing the filter
   * has to be instant and offline-cheap, and "is the rolodex itself empty?" —
   * the question the empty state turns on — is unanswerable from a response
   * that was already filtered.
   */
  const { data: vendors, isPending: vendorsPending } = useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const res = await client.api.vendors.$get({ query: {} });
      if (!res.ok) throw new Error('Failed to load vendors');
      return res.json();
    },
  });

  /**
   * The people she has already emailed and never filed as vendors, for the
   * other half of the "To" line's dropdown (EVE-88).
   *
   * Only once something is typed, which is the point of the `enabled` gate: no
   * request fires on page load, and clicking into the field to browse stays a
   * list of vendors. The server answers an empty query with the top of a
   * ranking by how often she mails an address, which on a real account is her
   * family, her accountant and a Send-to-Kindle address.
   *
   * Already merged-safe: `searchContacts` leaves out every address that is in
   * her rolodex, so a florist she has both emailed and filed appears once, as
   * the rolodex row this page's own `vendors` query supplies.
   *
   * No Gmail connection and a send-only grant both answer `{ contacts: [] }`
   * with a 200, so there is no "not connected" branch to write — the dropdown
   * simply never has a second half. A *failure* has to land in the same place:
   * a typeahead that raises a toast per keystroke is worse than one that
   * suggests nothing, so this retries nothing and resolves to no contacts.
   *
   * No `POST /refresh` from here, deliberately. The harvest is already asked
   * for when she connects Gmail, from the Settings row, and on every open of
   * the Add Vendor dialog; a composer that kicked off a mailbox scan as a side
   * effect of typing a name would be the fourth and the least expected.
   */
  const { data: contactSuggestions } = useQuery({
    queryKey: ['gmail-contacts', debouncedQuery],
    queryFn: async () => {
      const res = await client.api['gmail-contacts'].$get({ query: { q: debouncedQuery } });
      if (!res.ok) throw new Error('Failed to load contacts');
      return res.json();
    },
    enabled: debouncedQuery !== '',
    retry: false,
    // Keeps the previous answer on screen while the next one is in flight.
    // Without it `data` drops to undefined between debounced queries, and the
    // dropdown she is reading blinks out and back every 150ms as she types.
    placeholderData: (previous) => previous,
  });

  const { data: templates, isFetching: templatesFetching } = useQuery({
    queryKey: ['templates', category],
    queryFn: async () => {
      const res = await client.api.templates.$get({ query: { category } });
      if (!res.ok) throw new Error('Failed to load templates');
      return res.json();
    },
  });

  /**
   * This wedding's customization of the category's template, if there is one.
   *
   * 404 is a resting state, not an error: most categories have never been
   * customized, and "not customized" is what sends the composer to the saved
   * template instead.
   */
  const { data: override, isFetched: overrideFetched, isFetching: overrideFetching } = useQuery({
    queryKey: ['template-override', weddingId, category],
    queryFn: async () => {
      const res = await client.api.weddings[':id']['template-override'][':category'].$get({
        param: { id: weddingId!, category },
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to load your customization');
      return res.json();
    },
    enabled: !!weddingId && !!category,
  });

  const { data: quota } = useQuery({
    queryKey: ['send-quota'],
    queryFn: async () => {
      const res = await client.api.inquiries.quota.$get();
      if (!res.ok) throw new Error('Failed to load quota');
      return res.json();
    },
  });

  /**
   * The wedding's coverage windows, for `{{coverage_hours}}`.
   *
   * `/:id/coverage` and deliberately not `/:id/timeline`, which also carries
   * the windows: that endpoint materializes the default twelve-item ladder on
   * its first read, so asking it here would make *opening the composer* the
   * thing that drafts a wedding's timeline. A planner who has never touched
   * the timeline would find one waiting, authored by a screen she only used to
   * mail vendors. This endpoint seeds nothing and answers `annotations: []`
   * for a wedding nobody has annotated.
   *
   * Every row arrives already resolved — the `hours` string a merge renders is
   * computed on the server, so nothing here re-does the wrap past midnight.
   */
  const { data: coverage } = useQuery({
    queryKey: ['wedding-coverage', weddingId],
    queryFn: async () => {
      const res = await client.api.weddings[':id'].coverage.$get({
        param: { id: weddingId! },
      });
      if (!res.ok) throw new Error('Failed to load coverage');
      return res.json();
    },
    enabled: !!weddingId,
  });

  /**
   * Category name to that category's window, which is how a recipient finds
   * their own hours.
   *
   * Free-text windows are not in here, and that is `coverageByCategory`'s
   * rule, not an accident of this memo: a window labelled "Venue access"
   * names a constraint on the day rather than a vendor, so there is no
   * recipient it could ever be the right answer for.
   */
  const coverageHours = useMemo(
    () => coverageByCategory(coverage?.annotations ?? []),
    [coverage],
  );

  useEffect(() => {
    if (!templateId && templates && templates.length > 0) setTemplateId(templates[0]!.id);
  }, [templates, templateId]);

  const template = templates?.find((t) => t.id === templateId) ?? null;

  /**
   * What the customization is derived from: the saved one if this category has
   * been customized, otherwise whichever template is selected.
   *
   * The identity, not the content. Seeding is keyed on this so that switching
   * templates in the picker re-seeds the draft, while a planner *typing* into
   * the draft does not — and so a refetch that returns the same customization
   * cannot wipe an edit in progress.
   *
   * A saved customization outranks the template: it belongs to the wedding,
   * and picking a different florist template is not a decision to throw away
   * what was written about this couple.
   */
  const source = override ?? template;
  const sourceKey = override ? `override:${override.id}` : template ? `template:${template.id}` : null;
  const seededKey = useRef<string | null>(null);

  useEffect(() => {
    // `overrideFetched` is the gate: seeding from the template before the
    // saved customization has arrived would flash the wrong text, and would be
    // what a fast planner saves straight over the top of.
    if (!source || !sourceKey || !overrideFetched) return;
    /**
     * And no seeding out of a copy that is on its way to being replaced
     * (EVE-69).
     *
     * A planner who edits a template and walks into the composer arrives on a
     * warm cache holding the version she just rejected, which react-query
     * hands over on the first render while it refetches behind. Seeding is
     * keyed on identity, so that stale copy would be the one that sticks: the
     * real text lands a tick later under the same key and is dropped. She
     * would be writing to vendors out of the version she replaced, with
     * nothing on screen saying so.
     *
     * Waiting costs a tick of the letter's own loading state and settles it
     * from whichever source actually won. It cannot cost an edit: a refetch
     * that arrives while she is typing still comes back under the key already
     * seeded, and the guard below is what turns it away.
     */
    if (templatesFetching || overrideFetching) return;
    if (seededKey.current === sourceKey) return;

    seededKey.current = sourceKey;
    setDraft({
      subject: source.subject,
      // A body stored as plain text — every template predating the rich editor
      // — is converted for editing. Nothing persists that conversion until the
      // planner presses Continue, so opening the composer rewrites nothing.
      body: source.bodyFormat === 'html' ? source.body : plainTextToHtml(source.body),
      bodyFormat: 'html',
    });
  }, [source, sourceKey, overrideFetched, templatesFetching, overrideFetching]);

  /** Vendors already inquired on for this wedding cannot be selected again —
   *  UNIQUE(wedding_id, vendor_id) would reject the batch, so surface it here
   *  rather than as a 409 after the planner has composed eight messages. */
  const alreadyInquired = useMemo(() => {
    const ids = new Set<string>();
    for (const row of wedding?.board ?? []) {
      for (const inq of row.inquiries) ids.add(inq.vendorId);
    }
    return ids;
  }, [wedding]);

  /**
   * Why a vendor cannot be picked, in one place, because two surfaces now ask.
   * The grid and the recipient field disagreeing about who is pickable would
   * be a bug the planner discovers at the review step.
   */
  const disabledReason = useCallback(
    (vendor: VendorOption): string | null => {
      if (alreadyInquired.has(vendor.id)) return 'Already inquired on this wedding';
      if (vendor.emails.length === 0) return 'No email address on file';
      return null;
    },
    [alreadyInquired],
  );

  /** The merge context is per-wedding; only the vendor fields vary per message. */
  const baseContext: MergeContext = useMemo(
    () => ({
      // Full names here, short names in the breadcrumb above: the reader of
      // this string is a vendor who has never heard of this couple and gets
      // one shot at knowing who they are quoting for.
      couple_names: wedding ? coupleFullName(wedding) : undefined,
      wedding_date: wedding?.weddingDate ? formatDate(wedding.weddingDate) : undefined,
      wedding_hours: formatWeddingHours(wedding?.startTime, wedding?.endTime) ?? undefined,
      venue: wedding?.venueName ?? undefined,
      guest_count: wedding?.guestCountEstimate ? String(wedding.guestCountEstimate) : undefined,
      planner_name: settings?.name,
      planner_business: settings?.businessName ?? undefined,
    }),
    [wedding, settings],
  );

  /**
   * What the letter's chips can already show an answer for.
   *
   * Everything this wedding knows is filled in where it stands, so the planner
   * reads the sentence a vendor will read rather than merging it in their
   * head. The per-recipient fields are the exception on purpose: one template
   * goes to the whole batch, so there is no single answer to put there —
   * filling the greeting with whoever happens to be first, or the hours with
   * the first window drawn, would make a specimen of one message out of the
   * shape of eight. They stay named until the previews, where each vendor has
   * their own.
   */
  const resolveVariable = useCallback(
    (name: string) =>
      isPerRecipientVariable(name)
        ? undefined
        : (baseContext as Record<string, string | undefined>)[name],
    [baseContext],
  );

  /**
   * The merge context of each picked vendor, built once and read twice: the
   * messages below are rendered through it, and the previews' own variable
   * palette inserts out of it. Keyed off the picks rather than off the text,
   * so a keystroke in one card does not hand every other card a new resolver.
   */
  const vendorContexts = useMemo(() => {
    const map: Record<string, MergeContext> = {};
    for (const vendorId of selected) {
      const vendor = vendors?.find((v) => v.id === vendorId);
      map[vendorId] = {
        ...baseContext,
        vendor_name: vendorGreetingName(vendor?.contactName, vendor?.businessName),
        vendor_business: vendor?.businessName,
        // Here rather than in `baseContext`, and that placement is the whole
        // feature. `coverage_hours` is the one merge value that varies by
        // recipient while describing the wedding rather than the vendor: the
        // photographer is booked 11:00 AM to 8:00 PM and the florist is not,
        // so it has to be looked up per message from the vendor's own
        // category. In `baseContext` it would be one string for the entire
        // batch — most likely the first window drawn, mailed to a florist as
        // if it were hers.
        //
        // `undefined` for a vendor with no category, or with a category
        // nobody has drawn a window for. No special case: `renderTemplate`
        // leaves the token visible and the send gate below refuses the batch,
        // which is the right outcome — "I have you booked
        // {{coverage_hours}}" must never reach an inbox.
        coverage_hours: vendor?.primaryCategory
          ? coverageHours.get(vendor.primaryCategory)
          : undefined,
      };
    }
    return map;
  }, [selected, vendors, baseContext, coverageHours]);

  /** The same contexts as lookups, for the previews' palette. */
  const vendorResolvers = useMemo(() => {
    const map: Record<string, (name: string) => string | undefined> = {};
    for (const [vendorId, context] of Object.entries(vendorContexts)) {
      const values = context as Record<string, string | undefined>;
      map[vendorId] = (name) => values[name];
    }
    return map;
  }, [vendorContexts]);

  const composed = useMemo(() => {
    if (!template || !draft) return [];
    return selected.map((vendorId, index) => {
      const vendor = vendors?.find((v) => v.id === vendorId);
      const context = vendorContexts[vendorId] ?? baseContext;

      // Vary the opening before merging so a variant containing a merge
      // variable still resolves. The opening lines stay with the template —
      // they are about not looking like a blast, which is a property of the
      // batch rather than of this wedding.
      // Chip spans off first: a value merged *inside* one comes back into the
      // per-vendor letter as the chip's own name and serializes out as the
      // token again, so a planner who fixed a comma there would un-merge the
      // message. See `stripVariableChips`.
      const bodyWithOpening = applyOpeningVariant(
        stripVariableChips(draft.body),
        template.openingVariants,
        index,
      );
      const subject = renderTemplate(draft.subject, context);
      // Merged as html, so a couple called "Smith & Nguyễn" is escaped on the
      // way in rather than arriving as a broken entity.
      const body = renderTemplate(bodyWithOpening, context, draft.bodyFormat);

      // No signature is appended here. The server attaches the planner's HTML
      // signature to every outbound message on both send paths, as its own
      // multipart block with inline images — something a plain-text body in a
      // textarea cannot carry. Pasting a text copy in as well is how a planner
      // ends up signing off twice. `SignaturePreview` below shows them what
      // the server will add.
      const perVendor = edits[vendorId];
      return {
        vendorId,
        vendorName: vendor?.businessName ?? 'Unknown vendor',
        email: vendor?.emails.find((e) => e.isPrimary)?.email ?? vendor?.emails[0]?.email ?? null,
        subject: perVendor?.subject ?? subject.text,
        body: perVendor?.body ?? body.text,
        bodyFormat: draft.bodyFormat,
        // Issues are recomputed from the *current* text, so an edit that fixes
        // a blank variable clears the error without a round trip.
        issues: [...subject.issues, ...body.issues],
        edited: !!perVendor,
      };
    });
    // `settings` is not a dependency: the planner's name and business reach the
    // merge through `baseContext`, and the signature is no longer the client's
    // business at all.
  }, [template, draft, selected, vendors, baseContext, vendorContexts, edits]);

  /**
   * The merge variables this wedding's draft still leaves empty.
   *
   * Read off the draft rather than the template: the letter below is live, so
   * these are blanks the planner types over right where she is told about
   * them, and warning about text that is no longer on the screen is noise.
   * Per-recipient fields are never blanks here — they fill in per message,
   * which the To line already shows — so flagging them would make a problem
   * out of the normal case.
   *
   * `isPerRecipientVariable`, not a `vendor_` prefix test. The prefix was a
   * proxy that held only while every such variable was about the vendor's own
   * details; `coverage_hours` is read from the recipient's category and fills
   * in per message while carrying no prefix to match. On the old test this
   * caption told a planner whose every vendor has a window that coverage
   * hours were "still blank" and to go fill them in on the wedding — where
   * they already were.
   */
  const draftBlanks = useMemo(() => {
    if (!draft) return [];
    const subject = renderTemplate(draft.subject, baseContext);
    // The opening variant is applied first, exactly as the send applies it:
    // `{{opening}}` is resolved from the template's variants, or struck out
    // when it has none, so reporting it as a blank the planner has to fill
    // would be reporting the normal case as a fault.
    const body = renderTemplate(
      applyOpeningVariant(draft.body, template?.openingVariants ?? [], 0),
      baseContext,
      draft.bodyFormat,
    );
    return [
      ...new Set(
        [...subject.issues, ...body.issues]
          .map((issue) => issue.variable)
          .filter((name) => !isPerRecipientVariable(name)),
      ),
    ];
  }, [draft, template, baseContext]);

  /**
   * A token is looked for in the body's *text*, not its markup. In a rich body
   * a hand-typed token can end up split across tags —
   * `{{vendor_name<strong>}}</strong>` — which a scan of the html reads as
   * ordinary prose and a vendor reads as `{{vendor_name}}`. The server checks
   * the same way, so what blocks a send here is exactly what blocks it there.
   */
  const hasUnresolved = (m: { subject: string; body: string; bodyFormat: BodyFormat }) =>
    /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(`${m.subject}\n${bodyToText(m.body, m.bodyFormat)}`);

  /**
   * What Gmail will still accept from this mailbox today — the same number the
   * send runner enforces server-side, read here so that a batch is never
   * composed which cannot go out.
   *
   * Null when no mailbox is attached: the daily cap is not that account's
   * problem, and `canSend` already refuses the send for the reason that is.
   */
  const headroom = quota?.connected ? quota.remaining : null;
  /** The mailbox the cap belongs to, named in the pop-up so a planner with two
   *  accounts knows which one is full. */
  const mailboxEmail = quota?.connected ? quota.email : null;
  const dailyCap = quota?.connected ? quota.cap : null;

  /**
   * Every way onto the To line comes through here — the field, the rolodex
   * grid, and a vendor created mid-batch.
   *
   * The refusal is at the moment of picking rather than the moment of sending
   * (EVE-73). A planner who picks twelve vendors against three remaining
   * sends, writes twelve messages and *then* meets the cap has lost the work;
   * one who cannot pick the fourth has lost a click. So the selection is left
   * exactly as it was and the pop-up says what happened — the same shape as
   * the MAX_BATCH_SIZE cap, which also refuses the pick rather than warning
   * about it afterwards.
   *
   * Only growth is checked. Removing a recipient has to stay possible while
   * the batch is over the cap: it is the way back out.
   */
  const changeSelection = useCallback(
    (next: string[]) => {
      if (headroom !== null && next.length > selected.length && next.length > headroom) {
        setLimitHit('pick');
        return;
      }
      setSelected(next);
    },
    [headroom, selected],
  );

  const liveIssues = composed.filter(hasUnresolved);
  const missingEmail = composed.filter((m) => !m.email);
  const overQuota = quota?.connected ? composed.length > quota.remaining : false;

  /**
   * The cap can also be met *after* the picking — the poller drains the queue
   * while she writes the letter, and the quota query refetches behind her. The
   * selection-time guard above cannot catch that, and `canSend` below turns the
   * Send button off for it, so the same pop-up says why. Once: a dialog that
   * reopens every render is a page you cannot leave.
   */
  useEffect(() => {
    if (step !== 'review' || !overQuota) {
      // Armed again as soon as the batch fits, so a planner who removes a
      // message and meets the cap a second time is told a second time.
      if (!overQuota) warnedOverQuota.current = false;
      return;
    }
    if (warnedOverQuota.current) return;
    warnedOverQuota.current = true;
    setLimitHit('batch');
  }, [step, overQuota]);
  // The mailbox is the first condition, not the last: without it the send
  // fails at the API after the planner has written everything. `quota` is
  // already the send path's own view of the connection, so gating on it here
  // adds no second source of truth about whether Gmail is attached.
  const canSend =
    quota?.connected === true &&
    composed.length > 0 &&
    liveIssues.length === 0 &&
    missingEmail.length === 0 &&
    !overQuota;

  /**
   * Saves the customization on the way to the preview.
   *
   * Explicit rather than autosaved: this is the text of eight emails, and a
   * planner who tried a sentence and backed out must not find it waiting for
   * them next time. Pressing Continue is the moment they mean it.
   */
  const saveOverride = useMutation({
    mutationFn: async () => {
      if (!draft) return null;
      const res = await client.api.weddings[':id']['template-override'][':category'].$put({
        param: { id: weddingId!, category },
        json: {
          templateId,
          subject: draft.subject,
          body: draft.body,
          bodyFormat: draft.bodyFormat,
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Could not save your changes');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-override', weddingId, category] });
    },
    // A failed save must not block the preview: the draft is still in memory
    // and the messages below are built from it either way. Say so and carry on
    // — the alternative is a planner stuck on a screen with no way forward.
    onError: (err: Error) =>
      toast.error(`${err.message}. Your changes are still here, but they were not saved.`),
  });

  /** Back to the template as written. The only way out once a category has
   *  been customized, so it deletes the row rather than blanking it. */
  const resetOverride = useMutation({
    mutationFn: async () => {
      const res = await client.api.weddings[':id']['template-override'][':category'].$delete({
        param: { id: weddingId!, category },
      });
      // A 404 means there was nothing saved to begin with — the planner is
      // reverting a draft they never continued past. That is a success.
      if (!res.ok && res.status !== 404) throw new Error('Could not restore the template');
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-override', weddingId, category] });
      if (template) {
        setDraft({
          subject: template.subject,
          body:
            template.bodyFormat === 'html'
              ? template.body
              : plainTextToHtml(template.body),
          bodyFormat: 'html',
        });
      }
      // Per-vendor edits were made against text that no longer exists.
      setEdits({});
      toast.success('Back to the saved template.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      const res = await client.api.inquiries.batch.$post({
        json: {
          weddingId: weddingId!,
          category,
          messages: composed.map((m) => ({
            vendorId: m.vendorId,
            subject: m.subject,
            body: m.body,
            bodyFormat: m.bodyFormat,
          })),
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Send failed');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['wedding', weddingId] });
      queryClient.invalidateQueries({ queryKey: ['send-quota'] });
      toast.success(
        `${composed.length} ${composed.length === 1 ? 'inquiry' : 'inquiries'} queued — sending over the next few minutes.`,
      );
      navigate(`/weddings/${weddingId}/batches/${data.batch.id}`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!wedding) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link
          to={`/weddings/${weddingId}`}
          className="label-caps text-xs text-muted-foreground hover:text-foreground"
        >
          ← {coupleShortName(wedding)}
        </Link>
        <h1 className="display-tight text-3xl font-semibold">{category} inquiries</h1>
      </div>

      {/* One dialog for every way into it. The recipient field's "Add …" row,
          a contact she picked off the same dropdown, and the grid's Add vendor
          button all open this form, so the duplicate check and the required
          fields cannot drift between them — and what she has already given is
          carried in as a prefill rather than asked for twice. Which field each
          half lands in was decided where the text was, so there is nothing to
          work out from the shape of a string here. */}
      <AddVendorDialog
        open={creatingFrom !== null}
        onOpenChange={(v) => setCreatingFrom(v ? (creatingFrom ?? {}) : null)}
        defaultCategory={category}
        defaultBusinessName={creatingFrom?.businessName}
        defaultEmail={creatingFrom?.email}
        onCreated={(vendor) => {
          // A vendor created from inside a half-built batch was created to be
          // mailed — whether the planner typed their name into the field above
          // or pressed Add vendor in the grid. Creating them and then not
          // addressing them is a step she has to repeat. Capped like every
          // other way in, so this is not the one path that sneaks past it.
          changeSelection(selected.length < MAX_BATCH_SIZE ? [...selected, vendor.id] : selected);
          // The text that became this vendor has been spent. Left in place it
          // would keep the grid narrowed to a name that is now a chip.
          setQuery('');
        }}
      />

      {/* Gmail's daily cap, said once and only when it actually blocks
          something (EVE-73). It replaces a standing "496 of 500 daily sends"
          line above the previews — a count of a thing that had not happened
          yet, read on every batch and acted on roughly never, sitting in the
          one place where the planner's attention belonged to the letter.

          A dialog rather than a toast because this is a refusal, not a
          remark: the pick she just made did not take, and that cannot be
          allowed to fade out of the corner of the screen unread. */}
      <AlertDialog
        open={limitHit !== null}
        onOpenChange={(open) => {
          if (!open) setLimitHit(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="display-tight text-2xl font-semibold">
              {headroom === 0
                ? 'Gmail’s daily limit is reached'
                : 'That’s as many as Gmail allows today'}
            </AlertDialogTitle>
            {/* The number is stated here and nowhere else, because here it is
                the thing she has to act on: how many of this batch can go
                today, and how many wait. */}
            <AlertDialogDescription>
              {headroom === 0 ? (
                <>
                  All {dailyCap} of today’s sends from {mailboxEmail} have gone out.
                  Gmail won’t take another from this mailbox until tomorrow, so this
                  batch has to wait.
                </>
              ) : limitHit === 'batch' ? (
                <>
                  {mailboxEmail} has {headroom} of its {dailyCap} daily sends left, and
                  this batch holds {composed.length}. Take a few off it and they can go
                  tomorrow instead.
                </>
              ) : (
                <>
                  {mailboxEmail} has {headroom} of its {dailyCap} daily sends left, and
                  this batch already holds {selected.length}. Send these now and add the
                  rest tomorrow.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {step === 'compose' ? (
        <>
          <Card className="rounded-3xl bg-card p-5 shadow-ambient">
            <h2 className="display-tight mb-3 text-lg font-semibold">Pick a template</h2>
            {/* Same tile vocabulary as an untouched category on the wedding
                board: a dashed outline is an option not taken yet, and picking
                one closes the border and fills it with the selected container
                colour. The radio is the real control, kept off-screen rather
                than replaced, so arrow keys and screen readers still see a
                radio group. */}
            {(templates ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No {category} template yet.{' '}
                <Link to="/templates" className="underline underline-offset-2 hover:text-foreground">
                  Write one
                </Link>{' '}
                and it shows up here.
              </p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {(templates ?? []).map((t) => {
                  const chosen = templateId === t.id;
                  // The subject a vendor would receive, not the one stored.
                  // A row of tiles all reading "{{couple_names}}" tells the
                  // planner nothing they didn't already know. A token that
                  // survives — a vendor field, always — loses its braces:
                  // there is no room on this line for a chip, and the chosen
                  // tile's sage background is the chip's own colour.
                  const subject = bareTokens(renderTemplate(t.subject, baseContext).text);
                  return (
                    <label
                      key={t.id}
                      className={[
                        'flex cursor-pointer items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm transition-colors',
                        'has-[:focus-visible]:border-primary has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40',
                        chosen
                          ? 'border-primary bg-[var(--color-primary-container)]'
                          : 'border-dashed border-border hover:border-primary hover:bg-[var(--color-surface-container-high)]',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="template"
                        className="sr-only"
                        checked={chosen}
                        onChange={() => setTemplateId(t.id)}
                      />
                      <span className="min-w-0">
                        <span className="block font-medium">{t.name}</span>
                        <span
                          className={[
                            'block truncate text-xs',
                            // Grey secondary text goes muddy on the sage
                            // container, so the chosen tile tints from the
                            // same hue instead.
                            chosen ? 'text-[var(--color-primary-dim)]' : 'text-muted-foreground',
                          ].join(' ')}
                        >
                          {subject}
                        </span>
                      </span>
                      <span
                        aria-hidden
                        className={[
                          'mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border',
                          chosen
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-[var(--color-surface-container-high)]',
                        ].join(' ')}
                      >
                        {chosen && <Check className="size-2.5" strokeWidth={3} />}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* The letter, written here. The tiles above choose what it
                starts as; this is that text, open for typing — subject, To
                line and body, in the shape they will arrive in. It used to be
                a read-only specimen with the editor a page further on
                (EVE-58), which split one act of writing across two screens and
                made the tiles a choice the planner could not act on where she
                made it. */}
            {draft && template && (
              <div className="mt-4 space-y-2 border-t border-border pt-4">
                {/* No caption over the letter: it is a letter, and saying so
                    is what a label above it would be for. The only thing that
                    earns a row here is the way back out — and only once there
                    is a saved version to climb out of. It deletes the row
                    rather than blanking it, so the category goes back to
                    following the template. */}
                {override && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={resetOverride.isPending}
                      onClick={() => resetOverride.mutate()}
                    >
                      Start over from the template
                    </Button>
                  </div>
                )}

                {/* Every line of it live, which is the whole shape of this
                    block: the planner writes a real message rather than
                    filling a picker above a picture of one.

                    One template still goes to the whole batch, so what is
                    written here is the batch's text — the subject and body each
                    vendor gets their own copy of, and the per-message versions
                    of them are still the review step's, where each vendor has
                    a name to merge in.

                    No ground under it and no sheet around it. Both were a
                    specimen's dress — a thing held away from the page, drawn
                    narrower than its container so it could be looked at. This
                    is the message being written, so it is the card: the
                    envelope's own hairlines are the structure inside it. The
                    edge around the whole thing is a heavier line than those:
                    one says where a field ends, the other says where the
                    letter does. */}
                <MessageSheet
                  idPrefix="customize"
                  subject={draft.subject}
                  onSubjectChange={(text) => setDraft((d) => (d ? { ...d, subject: text } : d))}
                  body={draft.body}
                  onBodyChange={(html) =>
                    setDraft((d) => (d ? { ...d, body: html, bodyFormat: 'html' } : d))
                  }
                  bodyLabel="Message"
                  resolveVariable={resolveVariable}
                  signatureHtml={settings?.emailSignatureHtml}
                  // The live "To" line is this step's alone: on the previews
                  // the recipient is settled and is the card's own heading.
                  headerRows={
                    <>
                      {/* The To line proper: label and recipients on the line
                          itself, because that is one line of a letter and not
                          a stack of form controls. The counter and the way
                          into the rolodex sit on a second row beneath the
                          chips — they are about the line rather than part of
                          it, and a recipient list that grows to two rows of
                          chips should push them down rather than squeeze
                          against them.

                          Its own band under a hairline, rather than stacked
                          against the subject on shared padding: the subject is
                          a specimen and this row is live, and a rule is how a
                          mail client says where one envelope field ends and
                          the next begins. The same weight as the rule under
                          the whole header, because both are separating header
                          rows — a heavier one here would read as a second,
                          stronger division inside a block that is one thing. */}
                      <div className="flex flex-wrap items-start gap-x-2 gap-y-1 border-t border-border px-4 py-3">
                        {/* A visible label, in the same `Label htmlFor` shape
                            as every other field in the composer — and "To",
                            because this *is* the message's To line.
                            `aria-labelledby` on the field points back at it so
                            the field's own built-in name stands down rather
                            than outranking the label she can see.

                            Quieter than the composer's other labels on
                            purpose: it is letterhead, and the subject line
                            above it keeps precedence. `font-normal` undoes the
                            primitive's `font-medium`, which is what the
                            subject uses to outrank this row. */}
                        <Label
                          htmlFor="inquiry-recipients"
                          id="inquiry-recipients-label"
                          className="mt-1 w-14 shrink-0 text-xs font-normal text-muted-foreground"
                        >
                          To
                        </Label>
                        <div className="min-w-0 flex-1 basis-40 space-y-1.5">
                          <VendorRecipientField
                            id="inquiry-recipients"
                            // No box, no fill, no focus lift: this one is drawn
                            // as a line of the letter. See the variant's own
                            // notes for what replaces the focus indicator.
                            variant="envelope"
                            aria-labelledby="inquiry-recipients-label"
                            // The counter is a sibling rather than part of the
                            // name; this is what connects it to the control it
                            // is counting. As a description, so it is read
                            // after "To" instead of becoming the label.
                            aria-describedby="inquiry-recipients-count"
                            vendors={vendors}
                            pending={vendorsPending}
                            selected={selected}
                            onChange={changeSelection}
                            query={query}
                            onQueryChange={setQuery}
                            category={category}
                            disabledReason={disabledReason}
                            max={MAX_BATCH_SIZE}
                            // Withheld while the debounced query is empty, even
                            // though the field gates its own contact rows: the
                            // field gates on the *live* query, so for the 150ms
                            // after her first keystroke the two disagree — it
                            // believes she has typed while this query is still
                            // keyed `''`. Belt and braces with the `enabled`
                            // gate on the query itself, and the cheaper of the
                            // two to be sure of.
                            contacts={debouncedQuery === '' ? undefined : contactSuggestions?.contacts}
                            onCreateRequest={(prefill) => setCreatingFrom(prefill)}
                          />
                        {/* Under the chips rather than beside them, and inside
                            the field's own column so it starts where the chips
                            start instead of under the "To". Spread apart: the
                            count is a running total the planner glances at,
                            the trigger is a thing she aims for, and putting
                            them next to each other makes her read one to find
                            the other. */}
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                          {/* Not folded into a heading. A heading is a
                              navigation landmark, and a count inside one
                              renames the landmark on every pick — not a place
                              a screen-reader user can learn to jump to. */}
                          <p
                            id="inquiry-recipients-count"
                            className="text-xs text-muted-foreground"
                          >
                            {selected.length} of {MAX_BATCH_SIZE} max
                          </p>

                          {/* An empty rolodex is the one thing this trigger
                              must not be left to say. "Browse rolodex (0)" is
                              a label that argues against opening it, so a
                              planner on her first account would read a dead
                              "To" line, a disabled Next and nothing that tells
                              her what to do. The trigger goes away and the
                              message comes up under the To line instead: a
                              control that can only ever open onto an apology is
                              worse than no control.

                              `vendors &&` on purpose — `undefined` is a failed
                              fetch as well as one in flight, and "your rolodex
                              is empty" is a lie to tell someone whose request
                              merely failed. That case keeps the trigger and
                              claims no size. */}
                          {!(vendors && vendors.length === 0) && (
                            /* A right-hand sheet rather than a disclosure under
                               this line. EVE-58 merges the template *editor*
                               onto this page, so the body below becomes a live
                               editor — and a disclosure expanding two hundred
                               rows between the To line and the body would shove
                               that editor down the page mid-composition. A
                               centered modal was rejected for covering the
                               message being composed.

                               `modal={false}` is what makes the sheet keep the
                               promise the disclosure was keeping: no overlay
                               dimming the letter, no focus trap, and — the
                               load-bearing part — the To field above stays
                               typeable while the grid is open. The field is the
                               only text input on this step and the grid narrows
                               by what is in it, so a modal would have forced
                               her to close the panel to type and reopen it to
                               see the result. Clicking away dismisses it like
                               any side panel; the field and its dropdown are
                               the one exemption, for that same reason. */
                            <Sheet open={browsing} onOpenChange={setBrowsing} modal={false}>
                              <SheetTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="-mr-2 h-7 px-2 text-xs text-muted-foreground"
                                >
                                  {/* No count until there is one to state.
                                      `(0)` while the query is in flight, or
                                      after it failed, reads as an empty
                                      rolodex — the one thing this trigger must
                                      not claim. */}
                                  Browse rolodex{vendors ? ` (${vendors.length})` : ''}
                                  {/* Points at where the panel comes from. The
                                      rotating chevron the disclosure used said
                                      "this expands in place", which is no
                                      longer what pressing it does. */}
                                  <ChevronRight className="size-3.5" />
                                </Button>
                              </SheetTrigger>
                              <SheetContent
                                side="right"
                                // Wide enough for the grid's two columns to be
                                // two columns; `gap-0` because the header and
                                // the scroll area below set their own spacing.
                                className="w-full gap-0 sm:max-w-xl"
                                onInteractOutside={(event) => {
                                  // Clicking away closes it, like any side
                                  // panel. The one exception is the recipient
                                  // field and its dropdown: this panel's own
                                  // description tells the planner to type in
                                  // that field to narrow this list, and a
                                  // panel that shuts when you follow its
                                  // instructions is a panel that lied. Every
                                  // other outside press — the letter, the
                                  // template tiles, the page behind — dismisses.
                                  const target = event.target as HTMLElement | null;
                                  if (
                                    target?.closest('[data-slot="combobox-chips"]') ||
                                    target?.closest('[data-slot="combobox-content"]')
                                  ) {
                                    event.preventDefault();
                                  }
                                }}
                                onEscapeKeyDown={(event) => {
                                  // Escape closes the innermost open thing,
                                  // and Radix cannot work out which that is
                                  // here. Its dismissable-layer stack only
                                  // knows about Radix layers, while the
                                  // recipient field's dropdown is base-ui's —
                                  // so an Escape pressed to shut that dropdown
                                  // reaches this panel as the highest layer
                                  // Radix knows of and takes the whole rolodex
                                  // down with it. Which is a real gesture, not
                                  // a hypothetical: the field stays typeable
                                  // with the panel open precisely so she can
                                  // narrow the grid from it.
                                  //
                                  // So: an Escape from outside this panel, on
                                  // a control that reports a popup of its own
                                  // open, belongs to that control. Anything
                                  // else — including an Escape from the To
                                  // field with no dropdown showing — still
                                  // closes the panel.
                                  const target = event.target as HTMLElement | null;
                                  if (
                                    target?.getAttribute('aria-expanded') === 'true' &&
                                    !target.closest('[data-slot="sheet-content"]')
                                  ) {
                                    event.preventDefault();
                                  }
                                }}
                              >
                                <SheetHeader className="border-b border-border">
                                  <SheetTitle className="display-tight text-lg font-semibold">
                                    Browse your rolodex
                                  </SheetTitle>
                                  <SheetDescription>
                                    Ticking a vendor adds them to the To line. Type in that
                                    field to narrow this list.
                                  </SheetDescription>
                                </SheetHeader>
                                {/* The panel scrolls, not the page behind it:
                                    two hundred rows is taller than any viewport
                                    and the letter has to stay where she left
                                    it. `scroll-slim` because a default
                                    scrollbar is a browser surface that belongs
                                    to no design system. */}
                                <div className="scroll-slim min-h-0 flex-1 overflow-y-auto p-4">
                                  <RolodexPicker
                                    vendors={vendors}
                                    pending={vendorsPending}
                                    selected={selected}
                                    onToggle={(id, next) =>
                                      changeSelection(
                                        next
                                          ? [...selected, id]
                                          : selected.filter((x) => x !== id),
                                      )
                                    }
                                    query={query}
                                    category={category}
                                    categoryFilter={categoryFilter}
                                    onCategoryFilterChange={setCategoryFilter}
                                    disabledReason={disabledReason}
                                    atCap={selected.length >= MAX_BATCH_SIZE}
                                    onAddVendor={() => setCreatingFrom({})}
                                  />
                                </div>
                              </SheetContent>
                            </Sheet>
                          )}
                          </div>
                        </div>
                      </div>

                      {vendors && vendors.length === 0 && <RolodexEmpty />}
                    </>
                  }
                />

                {draftBlanks.length > 0 && (
                  <p className="flex items-start gap-2 text-xs text-muted-foreground">
                    <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                    <span>
                      Still blank: {draftBlanks.map(variableLabel).join(', ')} — chipped above.
                      Fill them in on the wedding, or take them out of the message.
                    </span>
                  </p>
                )}
              </div>
            )}
          </Card>

          <div className="flex justify-end">
            {/* Saves this wedding's version on the way to the previews —
                explicit rather than autosaved: this is the text of eight
                emails, and a planner who tried a sentence and backed out must
                not find it waiting for them next time. Pressing this is the
                moment they mean it. */}
            <Button
              disabled={
                selected.length === 0 ||
                !template ||
                !draft?.subject.trim() ||
                !draft?.body.trim() ||
                saveOverride.isPending
              }
              onClick={() => {
                saveOverride.mutate();
                setStep('review');
              }}
            >
              Preview {selected.length === 1 ? 'message' : 'messages'}
            </Button>
          </div>
        </>
      ) : (
        <>
          {liveIssues.length > 0 && (
            <p className="flex items-center gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle className="size-4 shrink-0" />
              {liveIssues.length} {liveIssues.length === 1 ? 'message has' : 'messages have'}{' '}
              unfilled placeholders. Fill them in or edit them out — they can't be sent as-is.
            </p>
          )}

          <div className="space-y-3">
            {composed.map((msg, i) => {
              const isOpen = expanded === msg.vendorId;
              const hasIssue = hasUnresolved(msg);
              return (
                <Card
                  key={msg.vendorId}
                  className={`rounded-3xl bg-card p-5 shadow-ambient ${
                    hasIssue ? 'ring-1 ring-destructive/40' : ''
                  }`}
                >
                  <button
                    className="flex w-full items-center justify-between gap-3 text-left"
                    onClick={() => setExpanded(isOpen ? null : msg.vendorId)}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {i + 1}. {msg.vendorName}
                        {msg.edited && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            edited
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {msg.email} · {msg.subject}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasIssue ? (
                        <AlertCircle className="size-4 text-destructive" />
                      ) : (
                        <Check className="size-4 text-primary" />
                      )}
                      {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="mt-4 space-y-3 border-t border-border pt-4">
                      {/* The same letter the batch was written on, one per
                          vendor and merged for them. It was a stack of form
                          fields here, which made the previews look like a form
                          for editing the message rather than the message — and
                          the whole promise of this step is that what is on the
                          screen is what goes.

                          Which is also why the palette inserts the answer as
                          prose here rather than as a chip: this letter is
                          already one vendor's, so there is nothing left to
                          defer, and a bubble dropped into finished text claims
                          the line is still waiting for something. A chip is
                          still what arrives for anything with no answer — and
                          those are exactly the ones the send gate stops. */}
                      <MessageSheet
                        idPrefix={msg.vendorId}
                        resolveVariable={vendorResolvers[msg.vendorId]}
                        insertAsValue
                        subject={msg.subject}
                        onSubjectChange={(text) =>
                          setEdits((prev) => ({
                            ...prev,
                            [msg.vendorId]: {
                              subject: text,
                              body: prev[msg.vendorId]?.body ?? msg.body,
                            },
                          }))
                        }
                        body={msg.body}
                        onBodyChange={(html) =>
                          setEdits((prev) => ({
                            ...prev,
                            [msg.vendorId]: {
                              subject: prev[msg.vendorId]?.subject ?? msg.subject,
                              body: html,
                            },
                          }))
                        }
                        bodyLabel={`Message to ${msg.vendorName}`}
                        signatureHtml={settings?.emailSignatureHtml}
                        minHeight="min-h-[12rem]"
                      />

                      <div className="flex justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setEdits((prev) => {
                              const next = { ...prev };
                              delete next[msg.vendorId];
                              return next;
                            })
                          }
                        >
                          Reset to template
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelected((prev) => prev.filter((x) => x !== msg.vendorId))}
                        >
                          Remove from batch
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-4">
            <Button variant="outline" onClick={() => setStep('compose')}>
              Back
            </Button>
            <Button disabled={!canSend || send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? 'Queueing…' : 'Send'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
